import React from 'react'
import { debounce, uniq, cloneDeep } from 'lodash'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'
import { useHistory } from 'react-router'
import moment from 'moment'

import { TaskAPIContextType } from './types'

import { hydrateTask, dehydrateTask, createDataObject } from './helpers'
import {
  updateTask as updateTaskAPI,
  postTask as postTaskAPI,
  deleteTask as deleteTaskAPI,
  postComment as postCommentAPI,
  deleteComment as deleteCommentAPI,
  getSubtasks as getSubtasksAPI,
  getTask as getTaskAPI,
  lookupTasks,
  patchTasks as patchTasksAPI,
  uploadFileTask,
  getTaskMeta
} from '@SonarCX/lib/api'
import { chunkArray, uniqueArray } from '@SonarCX/utilities'
import {
  selectTasks,
  finishInitialLoad,
  update,
  deleteTask,
  propagateMerge as propagateMergeAction,
  updateFormat,
  updateLoading,
  clearLoading,
  tasksLoaded as tasksLoadedAction,
  queueSub,
  clearSub,
  updateCacheSWR,
  taskUpdated,
  patch,
  setCommentCount
} from '@SonarCX/slices/tasks'
import { fetchStatus } from '@SonarCX/slices/taskStatus'
import { fetchPriorities } from '@SonarCX/slices/priority'
import { fetchCategories, selectCategory } from '@SonarCX/slices/category'
import { useAgent } from '../AgentProvider'
import { fetchTypes } from '@SonarCX/slices/taskType'
import { useTaskComments } from '../TaskCommentsProvider'
import { useUserAPI } from '../UserAPIProvider'
import { useAuth } from '../AuthProvider'
import { useTaskSWRMutateCache } from '../TaskSWRMutateCache'
import { useTaskPageState } from '../TaskPageStateProvider'
import { AddErrorAlert } from '@SonarCX/slices/alert'
import { loadTaskActivities } from '@SonarCX/slices/activity'
import { QUICK_CDD_SUBTASKS } from '@SonarCX/components/molecules/ChatModals/TaskModal/'
import { Routes } from '@SonarCX/Routes'
import { useEnvironment } from '../EnvironmentProvider'
import useAudioRecorder from '@SonarCX/hooks/useAudioRecorder'

export const DEFAULT_EMBEDS = ['sub_tasks', 'comments', 'media']
export const CALLBACK_PHASES = {
  UPDATE: 'UPDATE'
}
const TaskAPIContext = React.createContext<TaskAPIContextType>(null)
const MAX_BULK_CHUNK_SIZE = 25
const DEBOUNCE_DELAY = 300

const TaskAPIProvider = ({ children }) => {
  const dispatch = useAppDispatch()
  const history = useHistory()
  const { env } = useEnvironment()

  const {
    startRecording,
    stopRecording,
    recordingBlob,
    isRecording,
    recordingTime,
    clearRecord
  } = useAudioRecorder()

  const {
    tasks: rawTasks,
    loaded: tasksLoaded,
    loading: tasksLoading,
    blockLoad,
    subQueue,
    updatedTask
  } = useAppSelector(selectTasks)
  const tasks = React.useMemo(() => cloneDeep(rawTasks), [rawTasks])
  const {
    statuses,
    loaded: taskStatusLoaded,
    loading: taskStatusLoading
  } = useAppSelector(storeState => storeState.taskStatus)
  const {
    priorities,
    loaded: priorityLoaded,
    loading: priorityLoading
  } = useAppSelector(storeState => storeState.priority)
  const {
    typesValues: types,
    loaded: taskTypeLoaded,
    loading: taskTypeLoading
  } = useAppSelector(storeState => storeState.taskType)
  const { loaded: categoryLoaded, loading: categoryLoading } =
    useAppSelector(selectCategory)

  const { setUserSession } = useAgent()
  const {
    handleEvent: handleCommentEvent,
    updateCounts,
    deleteComment: deleteTaskComment
  } = useTaskComments()
  const { loadUser } = useUserAPI()
  const { isLoggedIn } = useAuth()
  const { updateTasks } = useTaskSWRMutateCache()
  const { tab } = useTaskPageState()

  const [eventQueue, setEventQueue] = React.useState([])
  const [ignoreUpdates, setIgnoreUpdates] = React.useState([])
  const callbackCache = React.useRef(
    Object.values(CALLBACK_PHASES).reduce((acc, curr) => {
      acc[curr] = {}
      return acc
    }, {})
  )
  const [skipUpdateID, setSkipUpdateID] = React.useState<string[]>([])
  const inputFileRef = React.useRef(null)

  const isCddEnv = React.useMemo(() => env?.name.includes('CDD'), [env?.name])

  // Converts incoming tasks to correct format
  const formatTask: TaskAPIContextType['formatTask'] = React.useCallback(
    task => {
      const hydrated = hydrateTask(task, types, statuses, priorities)
      return hydrated
    },
    [statuses, types, priorities]
  )

  React.useEffect(() => {
    if (isLoggedIn && !taskStatusLoaded && !taskStatusLoading) {
      dispatch(fetchStatus())
    }
  }, [isLoggedIn, taskStatusLoaded, taskStatusLoading, dispatch])

  React.useEffect(() => {
    if (isLoggedIn && !taskTypeLoaded && !taskTypeLoading) {
      dispatch(fetchTypes())
    }
  }, [isLoggedIn, taskTypeLoaded, taskTypeLoading, dispatch])

  React.useEffect(() => {
    if (isLoggedIn && !priorityLoaded && !priorityLoading) {
      dispatch(fetchPriorities())
    }
  }, [isLoggedIn, priorityLoaded, priorityLoading, dispatch])

  React.useEffect(() => {
    if (isLoggedIn && !categoryLoaded && !categoryLoading) {
      dispatch(fetchCategories())
    }
  }, [isLoggedIn, categoryLoaded, categoryLoading, dispatch])

  React.useEffect(() => {
    // Read from storage after being set by the login
    const storedUser = JSON.parse(localStorage.getItem('session_user') || null)
    const storedToken = localStorage.getItem('session_token') || null
    setUserSession(storedUser, storedToken)
    // eslint-disable-next-line
  }, [])

  const showError = React.useCallback(
    err => {
      dispatch(AddErrorAlert({ err }))
    },
    [dispatch]
  )

  const [fetchedTasks, setFetchedTasks] = React.useState(false)

  // Updates starting state for tasks
  React.useEffect(() => {
    if (tasksLoaded) {
      return
    }

    if (!fetchedTasks) {
      dispatch(finishInitialLoad([]))
      updateCounts([])
      setFetchedTasks(true)
    }
  }, [tasksLoaded, dispatch, fetchedTasks, updateCounts])

  React.useEffect(() => {
    if (!fetchedTasks || !tasksLoaded) {
      return
    }

    dispatch(updateFormat(formatTask))
  }, [fetchedTasks, tasksLoaded, dispatch, formatTask])

  // Fetches users for this all tasks
  React.useEffect(() => {
    const ids = []
    Object.values(tasks).forEach((t: APITask) => {
      ids.push(t.created_by)
      ids.push(t.concerning_user_id)
      ids.push(t.assignee_ids ? [...t.assignee_ids] : [])
    })
    const unique = uniqueArray(ids.filter(Boolean))
    loadUser(unique)
  }, [tasks, loadUser])

  const addTask: TaskAPIContextType['addTask'] = React.useCallback(
    async (task, onFinish, tempAdd = false) => {
      const data = dehydrateTask(task)

      // temporarily adds task if it meets api requirements
      if (tempAdd && task.title.length <= 150) {
        const formatted = { ...formatTask(data), id: 'temp' }
        dispatch(update({ task: formatted }))
      }

      const onComplete = result => {
        localStorage.setItem('taskId', result.id)
        const formatted = formatTask(result)

        dispatch(update({ task: formatted, replaceTemp: tempAdd }))
        onFinish && onFinish(formatted)
      }

      if (task.id) {
        const id = task.id
        try {
          const res = await updateTaskAPI(id, data)

          onComplete(res)
        } catch (err) {
          console.error('Error updating tasks in TaskAPIProvider', err)
          showError(err)

          if (tempAdd) {
            dispatch(deleteTask('temp'))
          }
          // Throw the error again so it can be caught by the component that is using it!
          throw new Error(err)
        }
      } else {
        try {
          const res = await postTaskAPI(data)
          onComplete(res)
        } catch (err) {
          console.error('Post error for task in TaskAPIProvider', err)
          showError(err)

          if (tempAdd) {
            dispatch(deleteTask('temp'))
          }
          // Throw the error again so it can be caught by the component that is using it!
          throw new Error(err)
        }
      }
    },
    [formatTask, showError, dispatch]
  )

  const deleteSingleTask: TaskAPIContextType['deleteSingleTask'] =
    React.useCallback(
      async id => {
        try {
          await deleteTaskAPI(id)
          localStorage.setItem('taskId', id)
          dispatch(deleteTask(id))
        } catch (err) {
          console.error('Error deleting task in TaskAPIProvider', err)
          showError(err)
        }
      },
      [showError, dispatch]
    )

  const addComment: TaskAPIContextType['addComment'] = React.useCallback(
    async (taskId, comment) => {
      try {
        const response = await postCommentAPI(taskId, { content: comment })
        return response
      } catch (err) {
        console.error('Post error for comment in TaskAPIProvider', err)
        showError(err)
      }
    },
    [showError]
  )

  const deleteComment: TaskAPIContextType['deleteComment'] = React.useCallback(
    async (taskID, commentID) => {
      try {
        await deleteCommentAPI(taskID, commentID)
        setTimeout(() => {
          deleteTaskComment(taskID, commentID, count => {
            dispatch(setCommentCount({ id: taskID, count }))
            updateTasks(tab, tasks[taskID])
          })
        }, 1500)
      } catch (err) {
        console.error('Delete error for comment in TaskAPIProvider', err)
        showError(err)
      }
    },
    [deleteTaskComment, dispatch, showError, updateTasks, tab, tasks]
  )

  const pushTask: TaskAPIContextType['pushTask'] = React.useCallback(
    (
      task,
      force = false,
      shouldUpdate = true,
      onSubCountDiffer = null,
      extraArgs = {}
    ) => {
      if (task.id in tasks && !force) {
        return
      }

      const time = new Date().toISOString()
      const newTask = { ...task }
      newTask.created_at = task.created_at ?? time
      newTask.updated_at = task.updated_at ?? time
      const formatted = formatTask(newTask)
      dispatch(
        update({
          task: formatted,
          updateSubCount: shouldUpdate,
          onSubCountDiffer,
          ...extraArgs
        })
      )
    },
    [tasks, formatTask, dispatch]
  )

  const getSubtasks: TaskAPIContextType['getSubtasks'] = React.useCallback(
    async (
      taskId,
      customFieldsToInclude = '',
      returnFull = false,
      force = false
    ) => {
      try {
        const task = tasks[taskId] ?? ({} as LocalTask)

        if (task.subtasksLoaded && !force) {
          if (returnFull) {
            const subs = []

            ;(task.subtasks ?? []).forEach(id => {
              const subTask = tasks[id]
              if (subTask) {
                subs.push(subTask)
              }
            })

            return subs
          }

          return task.subtasks ?? []
        }

        if (subQueue.includes(taskId)) {
          if (returnFull) {
            return []
          }

          return null
        }

        dispatch(queueSub(taskId))

        const subtasks = await getSubtasksAPI(
          taskId,
          DEFAULT_EMBEDS,
          customFieldsToInclude
        )
        task.id = taskId
        task.sub_tasks = subtasks.length
        task.subtasks = subtasks.map(st => st.id)
        task.subtasksContent = subtasks
        task.subtasksLoaded = true

        dispatch(clearSub(taskId))
        dispatch(update({ task, updateSubCount: true }))

        subtasks.forEach(t => pushTask(formatTask(t), true))

        if (returnFull) {
          return subtasks
        }

        return task.subtasks
      } catch (err) {
        showError(err)
      }
    },
    [tasks, subQueue, pushTask, showError, dispatch, formatTask]
  )

  const updateTask: TaskAPIContextType['updateTask'] = React.useCallback(
    async (id, newTask) => {
      try {
        const oldMedia = newTask.media ?? []
        const dh = dehydrateTask(newTask)
        const res = await updateTaskAPI(id, dh)
        // find task to replace content but not creation time
        const {
          created_at = new Date().toISOString(),
          sub_tasks = 0,
          subtasks = [],
          ...task
        } = tasks[id] ?? {}

        const formatted: LocalTask = {
          ...task,
          ...formatTask(res),
          created_at,
          sub_tasks,
          subtasks,
          media: oldMedia
        }
        localStorage.setItem('taskId', res.id)

        dispatch(update({ task: formatted, updateSubCount: true }))
        return true
      } catch (err) {
        console.error('Error updating task in TaskAPIProvider', err)
        // Throw the error again so it can be caught by the component that is using it!
        throw new Error(err)
      }
    },
    [tasks, formatTask, dispatch]
  )

  const deleteTag: TaskAPIContextType['deleteTag'] = React.useCallback(
    async (id, tag) => {
      const current = tasks[id]

      if (!current) {
        return
      }

      const modified = { ...current, tags: current.tags.filter(t => t !== tag) }

      const forAPI = dehydrateTask(modified)

      try {
        const task = await updateTaskAPI(id, forAPI)
        localStorage.setItem('taskId', task.id)
        const formatted = formatTask(task)
        dispatch(update({ task: formatted }))
      } catch (err) {
        console.error('Error updating task tags in TaskAPIProvider', err)
        showError(err)
      }
    },
    [tasks, formatTask, showError, dispatch]
  )

  // Replaces instances of from with to in areas with ids
  const propagateMerge: TaskAPIContextType['propagateMerge'] =
    React.useCallback(
      (from, to) => {
        dispatch(propagateMergeAction({ from, to }))
      },
      [dispatch]
    )

  const addSubtasks: TaskAPIContextType['addSubtasks'] = React.useCallback(
    async (values, parent_id, subtasks, isCddScenario) => {
      if (!parent_id || !subtasks) {
        return []
      }

      delete values.id

      // update parent task first
      // await getSubtasks(parent_id)

      // each subtask in array is being added here
      const subs = []
      const addedIDs = []
      const formattedSubtasks = subtasks.map((subt, index) => {
        if (
          (isCddScenario || isCddEnv) &&
          QUICK_CDD_SUBTASKS.some(t => t === subt)
        ) {
          return {
            ...values,
            media_ids: [],
            due_date: moment(values.due_date).subtract(7, 'days').toISOString(),
            details: '',
            title: subt,
            parent_id,
            sort_order: index + 1
          }
        }
        return {
          ...values,
          media_ids: [],
          details: '',
          title: subt,
          parent_id
        }
      })

      const promises = formattedSubtasks.map(subTask => {
        return new Promise((resolve, reject) => {
          try {
            addTask(subTask, result => {
              subs.push({
                ...subTask,
                id: result.id
              })
              resolve(result.id)
            })
          } catch (err) {
            showError(err)
            reject(err)
          }
        })
      })

      const ids = await Promise.allSettled(promises)
      for (const id of ids) {
        //@ts-ignore
        addedIDs.push(id?.value)
      }
      const current = tasks[parent_id]
      if (current) {
        current.sub_tasks = (current.sub_tasks ?? 0) + subtasks.length
        current.subtasks = [
          ...new Set([...(current.subtasks ?? []), ...addedIDs])
        ]

        dispatch(update({ task: current, updateSubCount: true }))
      }

      return subs
    },
    [tasks, isCddEnv, addTask, showError, dispatch]
  )

  const duplicateExistingSubtasks = React.useCallback(
    async (parent_id, existingSubtasks = []) => {
      if (!parent_id || existingSubtasks.length === 0) {
        return []
      }

      // each subtask in array is being added here
      const subs = []
      const addedIDs = []
      const formattedSubtasks = existingSubtasks.map(subt => {
        const { id, ...rest } = subt
        return {
          ...rest,
          parent_id
        }
      })

      const promises = formattedSubtasks.map(subTask => {
        return new Promise((resolve, reject) => {
          try {
            addTask(subTask, result => {
              subs.push({
                ...subTask,
                id: result.id
              })
              resolve(result.id)
            })
          } catch (err) {
            showError(err)
            reject(err)
          }
        })
      })

      const ids = await Promise.allSettled(promises)
      for (const id of ids) {
        //@ts-ignore
        addedIDs.push(id?.value)
      }
      const current = tasks[parent_id]
      if (current) {
        current.sub_tasks = (current.sub_tasks ?? 0) + existingSubtasks.length
        current.subtasks = [
          ...new Set([...(current.subtasks ?? []), ...addedIDs])
        ]

        dispatch(update({ task: current, updateSubCount: true }))
      }

      return subs
    },
    [addTask, dispatch, showError, tasks]
  )
  const fetchSubtasks: TaskAPIContextType['fetchSubtasks'] = React.useCallback(
    async id => {
      return await getSubtasks(id, '', true)
    },
    [getSubtasks]
  )

  const updateFetchedSubtasks: TaskAPIContextType['updateFetchedSubtasks'] =
    React.useCallback(
      id => {
        const current = tasks[id]
        if (current) {
          current.subtasksLoaded = true
          dispatch(update({ task: current }))
        }
      },
      [tasks, dispatch]
    )

  const deleteSubtasks: TaskAPIContextType['deleteSubtasks'] =
    React.useCallback(
      async (pending_ids, parent_id) => {
        const deletedIDs = await Promise.all(
          pending_ids.map(async id => {
            try {
              await deleteTaskAPI(id)
              dispatch(deleteTask(id))
              return id
            } catch (err) {
              console.error('Error deleting task in TaskAPIProvider', err)
              showError(err)
            }
          })
        )

        if (!(parent_id in tasks)) {
          return
        }

        // update parent task first
        await getSubtasks(parent_id)

        // remove any ids from user provider

        // Reset subtasks and update subtask count
        const current = tasks[parent_id]
        if (current) {
          current.sub_tasks = current.sub_tasks ?? 0
          current.subtasks = (current.subtasks ?? []).filter(
            id => !deletedIDs.includes(id)
          )
          dispatch(update({ task: current, updateSubCount: true }))
        }
      },
      [showError, tasks, getSubtasks, dispatch]
    )

  // Queues a task for loading
  const loadTask: TaskAPIContextType['loadTask'] = React.useCallback(
    id => {
      dispatch(updateLoading(Array.isArray(id) ? id : [id]))
    },
    [dispatch]
  )

  // Loads a set of tasks, applies chunking to calls
  const loadTasksAsync = React.useCallback(
    async (ids: string[]) => {
      const chunks = chunkArray(ids, MAX_BULK_CHUNK_SIZE)

      const taskResults = await Promise.all(
        chunks.map(async chunk => {
          if (chunk.length) {
            try {
              return (await lookupTasks(chunk, DEFAULT_EMBEDS)) as any[]
            } catch (err) {
              console.error('Error looking up tasks', err)
            }
          }
          return []
        })
      ).catch(() => {
        return []
      })

      if (!taskResults) {
        return []
      }

      return taskResults.flat().map(formatTask)
    },
    [formatTask]
  )

  // Loads any queued tasks
  React.useEffect(() => {
    const loadable = tasksLoading.filter(
      id => !(id in tasks) && !blockLoad.includes(id)
    )

    if (!tasksLoaded || !loadable.length) {
      return
    }

    const func = debounce(() => {
      dispatch(clearLoading(loadable))
      loadTasksAsync(loadable).then(tasks => {
        dispatch(tasksLoadedAction({ tasks, ids: loadable }))
      })
    }, DEBOUNCE_DELAY)

    func()

    return func.cancel
  }, [tasksLoaded, blockLoad, tasksLoading, tasks, loadTasksAsync, dispatch])

  // saving media on timeline task load
  const saveTaskMedia: TaskAPIContextType['saveTaskMedia'] = React.useCallback(
    async id => {
      const found = tasks[id]

      if (!found || found.media) {
        return
      }

      try {
        const withMedia = await getTaskAPI(id, ['media'])
        found.media = withMedia.media
        dispatch(update({ task: found }))
      } catch (err) {
        showError(err)
      }
    },
    [tasks, showError, dispatch]
  )

  // Task events from socket should come here
  const handleEvent: TaskAPIContextType['handleEvent'] = React.useCallback(
    msg => {
      setEventQueue(old => [...old, msg])
    },
    []
  )

  const processUpdateEvent = React.useCallback(
    async msg => {
      try {
        const { taskId } = msg

        const shouldSkip = skipUpdateID.includes(taskId)
        const shouldIgnore = ignoreUpdates.includes(taskId)

        // Used to stop swr from updating tables unnecessarily
        const shouldSkipHeavyUpdates = shouldSkip || shouldIgnore
        if (shouldSkipHeavyUpdates) {
          setSkipUpdateID(old => old.filter(oldID => oldID !== taskId))
        }

        const lastUpdatedTask = localStorage.getItem('taskId')

        // Call any registered callbacks on task update
        if (callbackCache.current && !shouldSkipHeavyUpdates) {
          Object.values(callbackCache.current[CALLBACK_PHASES.UPDATE]).forEach(
            func => {
              if (typeof func === 'function') {
                func()
              }
            }
          )
        }

        // Ignore updates because you likely triggered them locally and have the results
        if (shouldIgnore) {
          setIgnoreUpdates(old => old.filter(id => id !== taskId))
          return
        }

        // Perform updates
        const updated = formatTask(await getTaskAPI(taskId, DEFAULT_EMBEDS))
        // get meta data for task
        const taskMeta = await getTaskMeta(taskId)
        const fullyUpdated = { ...updated, meta: [...taskMeta] }
        dispatch(loadTaskActivities(taskId, true))

        // Flashes updates if current task is not the last one that updated
        if (lastUpdatedTask !== taskId) {
          dispatch(update({ task: fullyUpdated }))
        } else {
          localStorage.removeItem('taskId')
        }

        if (!shouldSkipHeavyUpdates) {
          // Update task in swr caches
          updateTasks(tab, fullyUpdated)
        }

        // Update task in system and trigger subtask update if subtask counts differ
        pushTask(
          fullyUpdated,
          true,
          true,
          async () => {
            try {
              const subtasks = await getSubtasksAPI(taskId, DEFAULT_EMBEDS)
              subtasks.forEach(t => pushTask(formatTask(t), true))
            } catch (err) {
              console.error('Error getting subtasks', err)
            }
          },
          { disableIgnore: true }
        )
      } catch (err) {
        console.error("Couldn't get updated task", err)
      }
    },
    [
      skipUpdateID,
      pushTask,
      tab,
      updateTasks,
      ignoreUpdates,
      dispatch,
      formatTask
    ]
  )

  // Handles socket events from the queue
  const processEvent = React.useCallback(
    async msg => {
      const { action_type: type } = msg
      if (type === 'update') {
        await processUpdateEvent(msg)
      } else if (type === 'comment') {
        handleCommentEvent(msg)
      } else {
        console.warn({ UNHANDLED_TASK: msg })
      }
    },
    [processUpdateEvent, handleCommentEvent]
  )

  React.useEffect(() => {
    if (Object.keys(updatedTask).length !== 0) {
      setTimeout(() => {
        dispatch(taskUpdated({}))
      }, 450)
    }
  }, [updatedTask, dispatch])

  // Processes and clears event queue
  React.useEffect(() => {
    if (!eventQueue.length) {
      return
    }

    // Using a timeout to allow duplicates to settle
    const timer = setTimeout(() => {
      const deduped = uniq(eventQueue)
      deduped.forEach(processEvent)
      setEventQueue([])
    }, 1000)

    return () => {
      clearTimeout(timer)
    }
  }, [eventQueue, processEvent])

  const closeTask: TaskAPIContextType['closeTask'] = React.useCallback(
    async (id, newTask) => {
      try {
        const dh = dehydrateTask(newTask)

        await updateTaskAPI(id, dh)
        localStorage.setItem('taskId', id)

        // we now show closed tasks
        // dispatchReducer({
        //   type: Actions.CLOSE,
        //   task: formatTask(current),
        //   id: id
        // })

        return true
      } catch (err) {
        console.error('Error updating task in TaskAPIProvider', err)
        showError(err)
        return false
      }
    },
    [showError]
  )

  // gets triggered when 'Close Task' button is clicked
  const markCloseTask: TaskAPIContextType['markCloseTask'] = React.useCallback(
    async (taskID, modal) => {
      if (!taskID) {
        return
      }

      const task = tasks[taskID]

      if (!task) {
        return
      }

      // add closing date of task
      const addCloseDate = {
        ...task,
        closed_at: new Date().toISOString()
      }

      try {
        const closed = await closeTask(taskID, addCloseDate)

        if (closed && !modal) {
          history.goBack()
        }
      } catch (err) {
        console.error('Error marking task complete', err)
      }
    },
    [closeTask, tasks, history]
  )

  const updateCacheFromSWR: TaskAPIContextType['updateCacheFromSWR'] =
    React.useCallback(
      data => {
        // @ts-ignore
        if (!Array.isArray(data?.data)) {
          // Paginated case
          if (Array.isArray(data)) {
            const tasks = data.flatMap(task => task.data)
            dispatch(updateCacheSWR({ tasks, format: formatTask }))
          }

          return
        }

        // @ts-ignore
        dispatch(updateCacheSWR({ tasks: data.data, format: formatTask }))
      },
      [formatTask, dispatch]
    )

  const registerCallback: TaskAPIContextType['registerCallback'] =
    React.useCallback((type, key, func) => {
      if (callbackCache.current && type in callbackCache.current) {
        callbackCache.current[type][key] = func
      }
    }, [])

  const unregisterCallback: TaskAPIContextType['unregisterCallback'] =
    React.useCallback((type, key) => {
      if (callbackCache.current && type in callbackCache.current) {
        delete callbackCache.current[type][key]
      }
    }, [])

  // Patches tasks locally and in the api
  const patchTasks: TaskAPIContextType['patchTasks'] = React.useCallback(
    async changes => {
      // Ignore flashing updates
      setIgnoreUpdates(old =>
        uniqueArray([...old, changes.map(change => change.id)])
      )
      dispatch(patch(changes))
      const chunks = chunkArray(changes, 100)
      return await Promise.all(
        chunks.map(async chunk => await patchTasksAPI({ tasks: chunk }))
      )
    },
    [dispatch]
  )

  const createMediaIds = React.useCallback(
    (modified, files, media_ids) => {
      // promise to upload all files first
      return new Promise((res, rej) => {
        if (!files.length) {
          res({ ids: media_ids, media: [] })
        }

        let toUpload = []
        let ids = []

        // creating array of files to upload
        files.forEach(f => {
          ids.push(f.id)
          if (f.name) {
            toUpload.push(f)
          }
        })

        // get file ids into array to upload
        let indicesToRemove = []
        // check if existing file has been removed
        modified.forEach(id => {
          if (!ids.includes(id)) {
            // find index that needs to be spliced
            const index = media_ids.indexOf(id)
            indicesToRemove.push(index)
          }
        })
        // convert array into object, remove and change back
        let obj = Object.assign({ ...modified })
        indicesToRemove.forEach(i => {
          delete obj[i]
        })
        obj = Object.keys(obj).map(key => obj[key])

        // clear array
        indicesToRemove = []

        media_ids = obj

        Promise.all(
          toUpload.map(async f => {
            try {
              const media_res = await uploadFileTask(f)
              if (media_res) {
                return media_res
              }
              rej('Error on file upload in TaskModal')
            } catch (err) {
              dispatch(AddErrorAlert(err))
              rej(`Error on file upload in TaskModal, ${err}`)
            }
          })
        )
          .then(results => {
            const newIds = results.map(result => result.id)
            res({ ids: uniqueArray(media_ids, newIds), media: results })
          })
          .catch(rej)
      })
    },
    [dispatch]
  )

  const handleTaskUpsert: TaskAPIContextType['handleTaskUpsert'] =
    React.useCallback(
      async (
        values,
        disableTaskCreation,
        session,
        newUserName,
        clientDetails,
        taskId,
        onTaskCreated,
        pending,
        setValues,
        setPending,
        setEditSubtasks,
        editSubtasks,
        setChanged,
        setDeleteConfirmation,
        startingVals,
        setLoadAdd,
        viewAfterCreate,
        modal,
        handleUpload,
        pendingAdd,
        setExistingSubtasks,
        setPendingAdd,
        uploadUser,
        existingSubtasks = []
      ) => {
        const { title, subtasks, file } = values

        let { media_ids = [] } = values
        if (media_ids.length !== values.media?.length) {
          media_ids = (values.media ?? []).map(item => item.id)
        }
        const modified = media_ids ? [...media_ids] : []

        if (title.trim().length === 0) {
          dispatch(
            AddErrorAlert({ err: { message: 'Title must not be empty.' } })
          )
          return
        }

        // @ts-ignore  uploading files/media
        const { ids, media: newMedia } = await createMediaIds(
          modified,
          file ?? [],
          media_ids
        )
        // new set of media ids to upload
        let mediaIds = ids
        const data = (await createDataObject(
          values,
          mediaIds,
          session,
          newUserName,
          uploadUser
        )) as LocalTask

        if (modal) {
          if (disableTaskCreation) {
            if (!data.concerning_user_id && clientDetails) {
              const updateUserInfo = { ...clientDetails }
              const { tasks } = updateUserInfo
              let idx = tasks.indexOf(taskId)

              tasks.splice(idx, 1)

              // updateUser(updateUserInfo)
            }

            const combinedMedia = [...(values.media ?? []), ...newMedia]
            data.media = combinedMedia.filter(m =>
              data.media_ids.includes(m.id)
            )
            await onTaskCreated(data, true)
            data.media = undefined

            // adding subtasks on edit modal
            if (subtasks.length) {
              await addSubtasks(data, taskId, subtasks)
            }

            if (pending.length) {
              deleteSubtasks(pending, taskId)
            }

            // empties subtasks array after adding tasks
            setValues(vals => ({ ...vals, subtasks: [] }))
            setPending([])
            setEditSubtasks(!editSubtasks)
            setChanged(false)
            setDeleteConfirmation(false)
            return
          }

          try {
            await addTask(
              data,
              async formatted => {
                const id = formatted.id

                // adding subtasks on new task modal
                if (existingSubtasks.length) {
                  await duplicateExistingSubtasks(id, existingSubtasks)
                } else if (subtasks.length) {
                  await addSubtasks(data, id, subtasks)
                }

                setValues(startingVals)
                setLoadAdd({ data: data, id, subtasks: subtasks })
                if (!disableTaskCreation && onTaskCreated) {
                  await onTaskCreated(formatted)
                }
                if (viewAfterCreate) {
                  history.push(`${Routes.TASKS}/${id}`)
                }
              },
              false
            )

            setChanged(false)
          } catch (err) {
            console.error('Post error for task in TaskModal', err)
            // Throw the error again so it can be caught by the component that is using it!
            throw err
          }
        } else {
          const combinedMedia = [...(values.media ?? []), ...newMedia]
          data.media = combinedMedia.filter(m => data.media_ids.includes(m.id))
          await handleUpload(data)

          if (pendingAdd.length > 0) {
            // replace temporarily added subtasks here to saved ones
            try {
              const added = await addSubtasks(
                { ...data, media_ids: [], sort_order: (subtasks + 1) * 10 },
                taskId,
                pendingAdd
              )

              setExistingSubtasks(e => {
                const subtasks = [...e, ...added]

                return [...subtasks]
              })
              setPendingAdd([])
            } catch (err) {
              // Throw the error again so it can be caught by the component that is using it!
              throw err
            }
          }

          if (pending.length > 0) {
            try {
              await deleteSubtasks(pending, taskId)

              setExistingSubtasks(e => {
                let existing = [...e]
                if (existing.length === pending.length) {
                  return []
                }

                existing = existing.filter(e => !pending.includes(e.id))

                return [...existing]
              })
              setPending([])
            } catch (err) {
              console.error('Error deleting subtasks', err)
              // Throw the error again so it can be caught by the component that is using it!
              throw err
            }
          }

          setChanged(false)
        }
      },
      [
        addSubtasks,
        addTask,
        createMediaIds,
        deleteSubtasks,
        dispatch,
        duplicateExistingSubtasks,
        history
      ]
    )

  return (
    <>
      <TaskAPIContext.Provider
        value={{
          tasks,
          loaded: tasksLoaded,
          updatedTask,
          fetchedTasks,
          setFetchedTasks,
          formatTask,
          addTask,
          deleteSingleTask,
          addComment,
          deleteComment,
          pushTask,
          getSubtasks,
          updateTask,
          deleteTag,
          propagateMerge,
          addSubtasks,
          fetchSubtasks,
          updateFetchedSubtasks,
          deleteSubtasks,
          loadTask,
          saveTaskMedia,
          handleEvent,
          closeTask,
          markCloseTask,
          updateCacheFromSWR,
          registerCallback,
          unregisterCallback,
          patchTasks,
          setSkipUpdateID,
          handleTaskUpsert,
          startRecording,
          stopRecording,
          recordingBlob,
          isRecording,
          recordingTime,
          clearRecord,
          inputFileRef
        }}
      >
        {children}
      </TaskAPIContext.Provider>
    </>
  )
}

export default TaskAPIProvider

export const useTaskAPI = () => React.useContext(TaskAPIContext)
